/**
 * HLOS-28 — Vault store TS (espelha `hlos/vault/vault.py` Python).
 *
 * Port TS literal do `Vault` dataclass (Python) com dual-key wrapping
 * (passphrase XOR mnemonic). Salva/carrega via abstração `VaultStorage`
 * (OPFS em produção, in-memory em testes).
 *
 * **Paridade byte-a-byte com Python**:
 *   - Mesmo schema JSON (`version`, `vault_id`, `kdf`, `salt_A`, `salt_B`,
 *     `ciphertext`, `wrapped_dek_1`, `wrapped_dek_2`)
 *   - Mesmo algoritmo de wrapping (HKDF-SHA256 com salt fixo)
 *   - Mesmas domain separation strings (`PASSPHRASE_INFO`, `MNEMONIC_INFO`)
 *   - Mesma lógica de unlock (qualquer das duas KEKs recupera o DEK)
 *
 * Garantias Constitucionais:
 *   - **CL-07** (Modelo = ativo): vault cifrado 100% local (OPFS), zero rede
 *   - **CL-08** (Ética fechada): zero key escrow, zero plaintext persistido.
 *     Master key NUNCA persiste — vive em memória (runtime), wiped em `lock()`
 *   - **CL-09** (Explicabilidade): log de qual key derivation path foi
 *     usado (passphrase vs mnemonic). `DecryptError` é intencionalmente
 *     vago (não vaza qual KEK falhou — anti side-channel)
 *   - **Princípio X** (Dignidade): auto-lock sem coação; user pode encerrar
 *     manualmente a qualquer momento
 *
 * Referência: `docs/architecture/IDENTITY_AND_KEY_MANAGEMENT.md` §2.1.
 */

import {
  argon2idDerive,
} from "./argon2";
import {
  encrypt as aesEncrypt,
  decrypt as aesDecrypt,
  AuthenticationFailedError,
} from "./aes-gcm";
import {
  generateMnemonic,
  mnemonicToSeed,
  secureWipe,
} from "./bip39";
import { hkdfSha256 } from "./hkdf";
import {
  ARGON2_MEMORY_COST_KIB,
  ARGON2_OUTPUT_LENGTH,
  ARGON2_PARALLELISM,
  ARGON2_TIME_COST,
  ARGON2_VERSION,
  BIP39_STRENGTH_BITS,
  DEK_BYTES,
  DEFAULT_AUTO_LOCK_MS,
  MIN_PASSPHRASE_LENGTH,
  MNEMONIC_INFO,
  PASSPHRASE_INFO,
  SALT_BYTES,
  VAULT_VERSION,
  WRAP_INFO,
} from "./constants";
import {

  validateUserId,
  vaultFilePath,
} from "./paths";

// Re-export para consumers que importam de store
export { InvalidVaultPathError, metaFilePath } from "./paths";

// ─────────────────────────────────────────────────────────────────────────────
// Erros (espelham Python `vault.py::exceptions`)
// ─────────────────────────────────────────────────────────────────────────────

/** Base error do vault. */
export class VaultError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VaultError";
  }
}

/**
 * Erro thrown quando decrypt falha (key errada, ciphertext tampering).
 * Espelha `DecryptError` (Python). Intencionalmente vago — nunca revela
 * se foi passphrase, mnemonic ou tampering (CL-08: zero info leakage).
 */
export class DecryptError extends VaultError {
  constructor(message: string = "vault decryption failed (wrong key or tampered ciphertext)") {
    super(message);
    this.name = "DecryptError";
  }
}

/** Erro thrown quando KDF params são inválidos. Espelha `KeyDerivationError`. */
export class KeyDerivationError extends VaultError {
  constructor(message: string) {
    super(message);
    this.name = "KeyDerivationError";
  }
}

/** Erro thrown quando passphrase é vazia (security guard). */
export class EmptyPassphraseError extends VaultError {
  constructor() {
    super(
      "passphrase must not be empty; empty passphrase defeats Argon2id KDF",
    );
    this.name = "EmptyPassphraseError";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

/** Parâmetros Argon2id (espelha Python `crypto.KDFParams`). */
export interface KDFParams {
  readonly timeCost: number;
  readonly memoryCostKib: number;
  readonly parallelism: number;
  readonly outputLength: number;
}

/** Default OWASP 2023+ baseline (espelha Python `KDFParams()`). */
export const DEFAULT_KDF_PARAMS: KDFParams = Object.freeze({
  timeCost: ARGON2_TIME_COST,
  memoryCostKib: ARGON2_MEMORY_COST_KIB,
  parallelism: ARGON2_PARALLELISM,
  outputLength: ARGON2_OUTPUT_LENGTH,
});

/** Schema JSON do vault (espelha `vault.Vault.to_json()`). */
interface VaultJson {
  version: number;
  vault_id: string;
  created_at: number;
  kdf: { m: number; t: number; p: number; L: number; version: number };
  salt_A: string; // base64
  salt_B: string; // base64
  ciphertext: string; // base64
  wrapped_dek_1: string; // base64
  wrapped_dek_2: string; // base64
}

/** Abstração de storage (OPFS em produção, in-memory em testes). */
export interface VaultStorage {
  /** Lê bytes do path (ou null se não existe). */
  read(path: string): Promise<Uint8Array | null>;
  /** Escreve bytes no path (sobrescreve se existe). */
  write(path: string, data: Uint8Array): Promise<void>;
  /** Remove path (no-op se não existe). */
  remove(path: string): Promise<void>;
  /**
   * HLOS-168-B: lista paths em um diretório. Aceita sync (string[])
   * ou async (Promise<string[]>) para compat com MemoryVaultStorage
   * legacy. Default impl retorna Promise.resolve([]).
   */
  list?(directory: string): string[] | Promise<string[]>;
}

/** Opções para `Vault.create()`. */
export interface VaultCreateOptions {
  /** ID do learner (usado em paths OPFS). */
  readonly userId: string;
  /** Passphrase do usuário. Não pode ser vazia. */
  readonly passphrase: string;
  /** Dados a cifrar dentro do vault. */
  readonly vaultData: Record<string, unknown>;
  /** Argon2id params (default = OWASP 2023+). */
  readonly kdfParams?: KDFParams;
  /** Storage (default = OPFSVaultStorage). */
  readonly storage?: VaultStorage;
}

/** Opções para `Vault.load()`. */
export interface VaultLoadOptions {
  /** ID do learner. */
  readonly userId: string;
  /** Storage (default = OPFSVaultStorage). */
  readonly storage?: VaultStorage;
}

/** Resultado de `Vault.create()`: vault + mnemonic (BIP-39 24 words). */
export interface VaultCreateResult {
  readonly vault: Vault;
  /** Mnemonic BIP-39 (24 palavras). Caller DEVE exibir UMA VEZ e guardar offline. */
  readonly mnemonic: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const BASE64_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

function bytesToBase64(bytes: Uint8Array): string {
  let result = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const b1 = bytes[i] ?? 0;
    const b2 = bytes[i + 1] ?? 0;
    const b3 = bytes[i + 2] ?? 0;
    const c1 = b1 >> 2;
    const c2 = ((b1 & 0x03) << 4) | (b2 >> 4);
    const c3 = ((b2 & 0x0f) << 2) | (b3 >> 6);
    const c4 = b3 & 0x3f;
    result +=
      BASE64_ALPHABET.charAt(c1) +
      BASE64_ALPHABET.charAt(c2) +
      (i + 1 < bytes.length ? BASE64_ALPHABET.charAt(c3) : "=") +
      (i + 2 < bytes.length ? BASE64_ALPHABET.charAt(c4) : "=");
  }
  return result;
}

function base64ToBytes(s: string): Uint8Array {
  // Decode manual (sem atob — atob é Latin-1, perde bytes > 0xFF).
  // `cleaned` exclui padding '='. `len * 3 / 4` é o número correto de bytes:
  //   - sem padding (0 '='): len bytes codificam len * 3/4 bytes
  //   - com padding: bytes = (len * 3 / 4) (a fórmula já considera)
  // Bug anterior: subtrair padding duas vezes resultava em 2 bytes faltando.
  const cleaned = s.replace(/=+$/, "");
  const len = cleaned.length;
  const outLen = Math.floor((len * 3) / 4);
  const out = new Uint8Array(outLen);
  let p = 0;
  for (let i = 0; i < len; i += 4) {
    const c1 = BASE64_ALPHABET.indexOf(cleaned.charAt(i));
    const c2 = BASE64_ALPHABET.indexOf(cleaned.charAt(i + 1));
    const c3 = i + 2 < len ? BASE64_ALPHABET.indexOf(cleaned.charAt(i + 2)) : -1;
    const c4 = i + 3 < len ? BASE64_ALPHABET.indexOf(cleaned.charAt(i + 3)) : -1;
    out[p++] = (c1 << 2) | (c2 >> 4);
    if (c3 >= 0) out[p++] = ((c2 & 0x0f) << 4) | (c3 >> 2);
    if (c4 >= 0) out[p++] = ((c3 & 0x03) << 6) | c4;
  }
  return out;
}

function cryptoRandomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * JSON.stringify replacer que ordena keys recursivamente.
 * Espelha Python `json.dumps(data, sort_keys=True)` (recursivo).
 *
 * Sem isso, sub-objetos são serializados com keys em ordem arbitrária,
 * quebrando paridade byte-a-byte com Python.
 */
function sortedJsonReplacer(_key: string, value: unknown): unknown {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    const sorted: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>).sort()) {
      sorted[k] = (value as Record<string, unknown>)[k];
    }
    return sorted;
  }
  return value;
}

// ─────────────────────────────────────────────────────────────────────────────
// Vault class
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Dual-key wrapped encrypted vault. Espelha Python `Vault` dataclass.
 *
 * Lifecycle:
 *   1. `Vault.create({ passphrase, vaultData, userId })` → { vault, mnemonic }
 *   2. (Caller deve exibir mnemonic UMA VEZ para user guardar offline)
 *   3. `vault.save()` (implícito no `create`)
 *   4. `vault.unlockWithPassphrase(p)` OU `vault.unlockWithMnemonic(m)`
 *   5. `vault.lock()` quando terminar
 *
 * Thread safety: NOT thread-safe (mutable runtime state). Crie instâncias
 * separadas por thread se necessário.
 */
export class Vault {
  // Public metadata
  public readonly vaultId: string;
  public readonly createdAt: number;
  public readonly kdfParams: KDFParams;

  // Encrypted blob (vault file content)
  public readonly saltA: Uint8Array;
  public readonly saltB: Uint8Array;
  public readonly ciphertext: Uint8Array;
  public readonly wrappedDek1: Uint8Array;
  public readonly wrappedDek2: Uint8Array;

  // Mutable runtime state (NUNCA serialized)
  private _dek: Uint8Array | null = null;
  private _plaintext: Uint8Array | null = null;
  private _unlockMethod: "create" | "passphrase" | "mnemonic" | "master_key" | null =
    null;
  private _lastActivityMs: number = 0;
  private _autoLockTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly _storage: VaultStorage;
  private readonly _userId: string;
  private readonly _autoLockMs: number;

  // ──────────── Constructor ────────────

  private constructor(params: {
    vaultId: string;
    createdAt: number;
    kdfParams: KDFParams;
    saltA: Uint8Array;
    saltB: Uint8Array;
    ciphertext: Uint8Array;
    wrappedDek1: Uint8Array;
    wrappedDek2: Uint8Array;
    dek: Uint8Array | null;
    plaintext: Uint8Array | null;
    unlockMethod: "create" | "passphrase" | "mnemonic" | null;
    userId: string;
    storage: VaultStorage;
    autoLockMs: number;
  }) {
    this.vaultId = params.vaultId;
    this.createdAt = params.createdAt;
    this.kdfParams = params.kdfParams;
    this.saltA = params.saltA;
    this.saltB = params.saltB;
    this.ciphertext = params.ciphertext;
    this.wrappedDek1 = params.wrappedDek1;
    this.wrappedDek2 = params.wrappedDek2;
    this._dek = params.dek;
    this._plaintext = params.plaintext;
    this._unlockMethod = params.unlockMethod;
    this._userId = params.userId;
    this._storage = params.storage;
    this._autoLockMs = params.autoLockMs;
    if (this._dek !== null && this._unlockMethod !== null) {
      this._scheduleAutoLock();
    }
  }

  // ──────────── Factory: create ────────────

  /**
   * Cria novo vault com passphrase + gera mnemonic BIP-39 (24 words).
   *
   * Espelha `Vault.create()` (Python). Caller DEVE:
   *   1. Exibir mnemonic UMA VEZ para user guardar offline
   *   2. NUNCA persistir mnemonic em plaintext após criação
   *   3. Tratar mnemonic como root secret (CL-08)
   */
  public static async create(options: VaultCreateOptions): Promise<VaultCreateResult> {
    validateUserId(options.userId);
    const { passphrase, vaultData, userId, kdfParams, storage } = options;

    if (!passphrase || passphrase.length < MIN_PASSPHRASE_LENGTH) {
      throw new EmptyPassphraseError();
    }

    const params = kdfParams ?? DEFAULT_KDF_PARAMS;
    const kdf = params;
    const stor: VaultStorage = storage ?? await (await import("./storage")).storageFactoryAsync();

    // 1. Generate fresh DEK + salts
    const dek = cryptoRandomBytes(DEK_BYTES);
    const saltA = cryptoRandomBytes(SALT_BYTES);
    const saltB = cryptoRandomBytes(SALT_BYTES);

    // 2. Derive KEK_1 from passphrase (Argon2id)
    const passphraseBytes = new TextEncoder().encode(passphrase);
    const kek1 = await argon2idDerive(
      passphraseBytes,
      saltA,
      PASSPHRASE_INFO,
      kdf,
    );

    // 3. Generate mnemonic + derive KEK_2
    const mnemonic = generateMnemonic(BIP39_STRENGTH_BITS);
    const seed512 = mnemonicToSeed(mnemonic, "");
    const kek2 = await argon2idDerive(seed512, saltB, MNEMONIC_INFO, kdf);

    // 4. Derive wrap keys via HKDF
    const wrapKey1 = await hkdfSha256(kek1, WRAP_INFO, 32);
    const wrapKey2 = await hkdfSha256(kek2, WRAP_INFO, 32);

    // 5. Wrap DEK with each wrap key
    const wrappedDek1 = await aesEncrypt(wrapKey1, dek);
    const wrappedDek2 = await aesEncrypt(wrapKey2, dek);

    // 6. Encrypt vaultData with DEK
    const plaintext = new TextEncoder().encode(
      JSON.stringify(vaultData, sortedJsonReplacer),
    );
    const ciphertext = await aesEncrypt(dek, plaintext);

    // 7. Create Vault instance (unlocked by default após create)
    const vault = new Vault({
      vaultId: crypto.randomUUID(),
      createdAt: Date.now() / 1000,
      kdfParams: kdf,
      saltA,
      saltB,
      ciphertext,
      wrappedDek1,
      wrappedDek2,
      dek,
      plaintext,
      unlockMethod: "create",
      userId,
      storage: stor,
      autoLockMs: DEFAULT_AUTO_LOCK_MS,
    });

    // 8. Persist to storage
    await vault.save();

    // 9. Best-effort wipe de intermediate key material (CL-08)
    for (const buf of [kek1, kek2, wrapKey1, wrapKey2, seed512]) {
      try {
        secureWipe(buf);
      } catch {
        // best-effort
      }
    }

    return { vault, mnemonic };
  }

  // ──────────── Persistence ────────────

  /**
   * Serializa vault metadata para JSON (espelha `Vault.to_json()`).
   * Plaintext NÃO é serializado (segurança).
   */
  public toJson(): string {
    const obj: VaultJson = {
      version: VAULT_VERSION,
      vault_id: this.vaultId,
      created_at: this.createdAt,
      kdf: {
        m: this.kdfParams.memoryCostKib,
        t: this.kdfParams.timeCost,
        p: this.kdfParams.parallelism,
        L: this.kdfParams.outputLength,
        version: ARGON2_VERSION,
      },
      salt_A: bytesToBase64(this.saltA),
      salt_B: bytesToBase64(this.saltB),
      ciphertext: bytesToBase64(this.ciphertext),
      wrapped_dek_1: bytesToBase64(this.wrappedDek1),
      wrapped_dek_2: bytesToBase64(this.wrappedDek2),
    };
    // `sortedJsonReplacer` (replacer recursivo) ordena chaves para paridade
    // byte-a-byte com Python `json.dumps(data, sort_keys=True)`. NÃO usamos
    // `JSON.stringify(obj, Object.keys(obj).sort(), 2)` (array filter) — esse
    // padrão produz JSON inválido em algumas versões de V8 (provavelmente bug).
    return JSON.stringify(obj, sortedJsonReplacer, 2);
  }

  /**
   * Carrega vault do storage. NÃO desbloqueia (deve chamar
   * `unlockWithPassphrase` ou `unlockWithMnemonic` depois).
   */
  public static async load(options: VaultLoadOptions): Promise<Vault> {
    validateUserId(options.userId);
    const stor: VaultStorage = options.storage ?? await (await import("./storage")).storageFactoryAsync();
    const json = await stor.read(vaultFilePath(options.userId));
    if (json === null) {
      throw new VaultError(`vault not found for userId="${options.userId}"`);
    }
    return Vault.fromJson(new TextDecoder().decode(json), options.userId, stor);
  }

  /**
   * Carrega vault de JSON string. NÃO desbloqueia.
   * Espelha `Vault.from_json()` (Python).
   */
  public static fromJson(
    json: string,
    userId: string,
    storage: VaultStorage,
    autoLockMs: number = DEFAULT_AUTO_LOCK_MS,
  ): Vault {
    const obj = JSON.parse(json) as VaultJson;
    if (obj.version !== VAULT_VERSION) {
      throw new DecryptError(
        `unsupported vault version: ${obj.version} (expected ${VAULT_VERSION})`,
      );
    }
    return new Vault({
      vaultId: obj.vault_id,
      createdAt: obj.created_at,
      kdfParams: {
        timeCost: obj.kdf.t,
        memoryCostKib: obj.kdf.m,
        parallelism: obj.kdf.p,
        outputLength: obj.kdf.L,
      },
      saltA: base64ToBytes(obj.salt_A),
      saltB: base64ToBytes(obj.salt_B),
      ciphertext: base64ToBytes(obj.ciphertext),
      wrappedDek1: base64ToBytes(obj.wrapped_dek_1),
      wrappedDek2: base64ToBytes(obj.wrapped_dek_2),
      dek: null,
      plaintext: null,
      unlockMethod: null,
      userId,
      storage,
      autoLockMs,
    });
  }

  /** Persiste vault para storage. Espelha `Vault.save()` (Python). */
  public async save(): Promise<void> {
    const json = this.toJson();
    await this._storage.write(
      vaultFilePath(this._userId),
      new TextEncoder().encode(json),
    );
  }

  // ──────────── Unlocking ────────────

  /** Desbloqueia com passphrase. Returns vaultData on success. */
  public async unlockWithPassphrase(passphrase: string): Promise<Record<string, unknown>> {
    this._touch();
    const kek = await argon2idDerive(
      new TextEncoder().encode(passphrase),
      this.saltA,
      PASSPHRASE_INFO,
      this.kdfParams,
    );
    try {
      return await this._unlockWithKek(kek, "passphrase");
    } finally {
      secureWipe(kek);
    }
  }

  /** Desbloqueia com mnemonic BIP-39. Returns vaultData on success. */
  public async unlockWithMnemonic(mnemonic: string): Promise<Record<string, unknown>> {
    this._touch();
    // validate_mnemonic() throws InvalidMnemonicError if invalid.
    const seed512 = mnemonicToSeed(mnemonic, "");
    const kek = await argon2idDerive(seed512, this.saltB, MNEMONIC_INFO, this.kdfParams);
    try {
      return await this._unlockWithKek(kek, "mnemonic");
    } finally {
      secureWipe(seed512);
      secureWipe(kek);
    }
  }

  /**
   * HLOS-51: Desbloqueia com master key (32 bytes) recuperada de biometric blob.
   *
   * Master key = DEK direto (bypassa KEK derivation). Usado por
   * BiometricKeyManager que decifrou o blob OPFS via ECDH + AES-GCM.
   *
   * Throws:
   *  - DecryptError se ciphertext não decifra com esta master key
   *  - ValueError se master_key não tem 32 bytes
   */
  public async unlockWithMasterKey(masterKey: Uint8Array): Promise<Record<string, unknown>> {
    if (!(masterKey instanceof Uint8Array)) {
      throw new VaultError(
        `masterKey must be Uint8Array, got ${typeof masterKey}`,
      );
    }
    if (masterKey.byteLength !== 32) {
      throw new VaultError(
        `masterKey must be 32 bytes, got ${masterKey.byteLength}`,
      );
    }
    this._touch();
    try {
      // Master key = DEK direto. Tenta decifrar ciphertext.
      return await this._unlockWithMasterKeyImpl(masterKey);
    } catch (e) {
      secureWipe(masterKey);  // best-effort
      throw e;
    }
  }

  private async _unlockWithMasterKeyImpl(masterKey: Uint8Array): Promise<Record<string, unknown>> {
    try {
      const plaintext = await aesDecrypt(masterKey, this.ciphertext);
      this._dek = masterKey;
      this._plaintext = plaintext;
      this._unlockMethod = "master_key";
      // HLOS-57: usar TextDecoder API correta (Uint8Array não tem `.decode()`).
      // Cópia defensiva (caller pode modificar masterKey após retornar).
      return JSON.parse(new TextDecoder().decode(plaintext));
    } catch (e) {
      throw new DecryptError("master_key unlock failed");
    }
  }

  /**
   * Lock vault: limpa DEK + plaintext da memória (CL-08 best-effort).
   * Cancela auto-lock timer.
   */
  public lock(): void {
    if (this._dek !== null) secureWipe(this._dek);
    if (this._plaintext !== null) secureWipe(this._plaintext);
    this._dek = null;
    this._plaintext = null;
    this._unlockMethod = null;
    this._cancelAutoLock();
  }

  /**
   * HLOS-48: retorna o DEK (Data Encryption Key) raw para passar ao kernel Python.
   *
   * ⚠️ SEGURANÇA: este bytes é a master key em memória. Caller DEVE:
   *   1. NÃO logar / NÃO printar / NÃO serializar
   *   2. Passar APENAS para js_process_turn(turn_json, master_key_hex)
   *      via Pyodide runPythonAsync (com encoding hex)
   *   3. NÃO armazenar em localStorage / sessionStorage / variáveis de debug
   *   4. Chamar lock() quando terminar para secureWipe
   *
   * Retorna cópia defensiva (não referência interna).
   * Throws se vault locked (DEK não em memória).
   */
  public getDekBytes(): Uint8Array {
    if (this._dek === null) {
      throw new VaultError(
        "Cannot get DEK: vault is locked. Call unlockWithPassphrase() first.",
      );
    }
    // Cópia defensiva — caller pode modificar sem afetar vault interno
    return new Uint8Array(this._dek);
  }

  /**
   * HLOS-48: retorna DEK como hex string (64 chars = 32 bytes).
   * Convenience para passar via Pyodide runPythonAsync string interpolation.
   *
   * Throws se vault locked.
   */
  public getDekHex(): string {
    const bytes = this.getDekBytes();
    let hex = "";
    for (let i = 0; i < bytes.length; i++) {
      hex += bytes[i]!.toString(16).padStart(2, "0");
    }
    return hex;
  }

  /** Returns vaultData (sem re-decifrar). Throws se locked. */
  public readData(): Record<string, unknown> {
    if (this._plaintext === null) {
      throw new VaultError("vault is locked (call unlockWithPassphrase or unlockWithMnemonic)");
    }
    return JSON.parse(new TextDecoder().decode(this._plaintext));
  }

  /**
   * Atualiza vaultData e re-cifra o vault. Espelha `Vault.write_data()`
   * (Python). Persiste imediatamente em OPFS.
   *
   * HLOS-44 (Sprint 4): persiste sessão entre dias. Constitutionalmente,
   * session snapshot NUNCA sai do device plaintext — fica cifrado com
   * a mesma DEK do vault (CL-07 + CL-08).
   *
   * @param data novo vaultData (substitui o anterior; merge no caller)
   * @throws VaultError se vault locked
   */
  public async writeData(data: Record<string, unknown>): Promise<void> {
    if (this._dek === null || this._plaintext === null) {
      throw new VaultError("vault is locked (call unlockWithPassphrase or unlockWithMnemonic)");
    }
    const plaintext = new TextEncoder().encode(JSON.stringify(data));
    this._plaintext = plaintext;
    // Re-cifra plaintext com a DEK atual. `encrypt` retorna blob único (nonce||ct||tag).
    const blob = await aesEncrypt(this._dek, plaintext);
    // ciphertext é readonly public; cast para mutation interna (Vault API design choice).
    (this as { ciphertext: Uint8Array }).ciphertext = blob;
    // Persiste vault.json com ciphertext atualizado.
    const meta = await this._storage.read(vaultFilePath(this._userId));
    if (meta === null) {
      throw new VaultError("vault metadata missing; cannot persist writeData");
    }
    const obj = JSON.parse(new TextDecoder().decode(meta));
    obj.ciphertext = bytesToBase64(blob);
    obj.updated_at = Date.now() / 1000;
    await this._storage.write(
      vaultFilePath(this._userId),
      new TextEncoder().encode(JSON.stringify(obj)),
    );
  }

  /** True se unlocked (DEK em memória). */
  public get isUnlocked(): boolean {
    return this._dek !== null && this._plaintext !== null;
  }

  /** Último método de unlock usado (passphrase / mnemonic / create). */
  public get unlockMethod(): string | null {
    return this._unlockMethod;
  }

  // ──────────── Private ────────────

  private async _unlockWithKek(
    kek: Uint8Array,
    method: "passphrase" | "mnemonic",
  ): Promise<Record<string, unknown>> {
    const wrapped = method === "passphrase" ? this.wrappedDek1 : this.wrappedDek2;
    const wrapKey = await hkdfSha256(kek, WRAP_INFO, 32);

    let dek: Uint8Array;
    try {
      dek = await aesDecrypt(wrapKey, wrapped);
    } catch (e) {
      // Intencionalmente vago — não vaza qual KEK falhou (anti side-channel).
      if (e instanceof AuthenticationFailedError) {
        throw new DecryptError(`${method} unlock failed`);
      }
      throw e;
    } finally {
      secureWipe(wrapKey);
    }

    let plaintext: Uint8Array;
    try {
      plaintext = await aesDecrypt(dek, this.ciphertext);
    } catch (e) {
      secureWipe(dek);
      if (e instanceof AuthenticationFailedError) {
        throw new DecryptError(`${method} unlock failed (ciphertext tamper?)`);
      }
      throw e;
    }

    this._dek = dek;
    this._plaintext = plaintext;
    this._unlockMethod = method;
    this._scheduleAutoLock();
    return JSON.parse(new TextDecoder().decode(plaintext));
  }

  private _touch(): void {
    this._lastActivityMs = Date.now();
    this._scheduleAutoLock();
  }

  private _scheduleAutoLock(): void {
    this._cancelAutoLock();
    if (this._autoLockMs <= 0 || this._dek === null) return;
    this._autoLockTimer = setTimeout(() => {
      if (Date.now() - this._lastActivityMs >= this._autoLockMs) {
        this.lock();
      }
    }, this._autoLockMs);
  }

  private _cancelAutoLock(): void {
    if (this._autoLockTimer !== null) {
      clearTimeout(this._autoLockTimer);
      this._autoLockTimer = null;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// OPFS Vault Storage (produção)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * OPFS Vault Storage — produção. Espelha ADR-0004 OPFS path conventions.
 *
 * Em produção, usa `navigator.storage.getDirectory()` para navegar OPFS.
 * Em testes (jsdom), `navigator.storage` não existe — usar
 * `MemoryVaultStorage` (mock in-memory).
 */
export class OPFSVaultStorage implements VaultStorage {
  private constructor(private readonly root: FileSystemDirectoryHandle) {}

  static async create(): Promise<OPFSVaultStorage> {
    if (typeof navigator === "undefined" || !navigator.storage) {
      throw new VaultError(
        "OPFSVaultStorage requires navigator.storage (browser/Node 20+ with --experimental-flag)",
      );
    }
    const root = await navigator.storage.getDirectory();
    return new OPFSVaultStorage(root);
  }

  async read(path: string): Promise<Uint8Array | null> {
    try {
      const parts = path.split("/").filter((p) => p.length > 0);
      let dir = this.root;
      for (let i = 0; i < parts.length - 1; i++) {
        dir = await dir.getDirectoryHandle(parts[i]!, { create: false });
      }
      const fileHandle = await dir.getFileHandle(parts[parts.length - 1]!, {
        create: false,
      });
      const file = await fileHandle.getFile();
      return new Uint8Array(await file.arrayBuffer());
    } catch (e) {
      // NotFoundError = arquivo não existe
      if (e instanceof Error && e.name === "NotFoundError") return null;
      throw e;
    }
  }

  async write(path: string, data: Uint8Array): Promise<void> {
    const parts = path.split("/").filter((p) => p.length > 0);
    let dir = this.root;
    for (let i = 0; i < parts.length - 1; i++) {
      dir = await dir.getDirectoryHandle(parts[i]!, { create: true });
    }
    const fileHandle = await dir.getFileHandle(parts[parts.length - 1]!, {
      create: true,
    });
    const writable = await fileHandle.createWritable();
    await writable.write(data as unknown as BufferSource);
    await writable.close();
  }

  async remove(path: string): Promise<void> {
    try {
      const parts = path.split("/").filter((p) => p.length > 0);
      let dir = this.root;
      for (let i = 0; i < parts.length - 1; i++) {
        dir = await dir.getDirectoryHandle(parts[i]!, { create: false });
      }
      await dir.removeEntry(parts[parts.length - 1]!, { recursive: false });
    } catch (e) {
      if (e instanceof Error && e.name === "NotFoundError") return; // no-op
      throw e;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Memory Vault Storage (testes)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Vault Storage in-memory — testes. Não persiste nada; cada VaultStorage é
 * isolada da outra.
 */
export class MemoryVaultStorage implements VaultStorage {
  private readonly files = new Map<string, Uint8Array>();

  async read(path: string): Promise<Uint8Array | null> {
    return this.files.get(path) ?? null;
  }

  async write(path: string, data: Uint8Array): Promise<void> {
    this.files.set(path, new Uint8Array(data));
  }

  async remove(path: string): Promise<void> {
    this.files.delete(path);
  }

  /** Helper de teste: lista todos os paths. */
  list(): string[] {
    return Array.from(this.files.keys());
  }
}
